import {
  FLAG_PENDING_ATTR,
  FLAG_PENDING_FALLBACK_MS,
} from "@/libs/locale/locale-ready";
import {
  COUNTRY_CODE_KEY,
  LANGUAGE_PREFERENCE_KEY,
} from "@/constants/CookieValues";
import {
  BHAPTICS_COOKIE_OPTS,
  deleteCookieAllScopes,
  parseCountryCode,
} from "@/libs/locale/cookie";
import { GLOBALE_SCRIPT_ID, isGeMarkedCountry } from "@/libs/global-e/marker";
import { syncGlobalEDataCookie } from "@/libs/locale/global-e-cookie";
import {
  getResolvedCountryCode,
  LANGUAGE_NAME_MAP,
  LanguageCode,
  parseLocale,
} from "@/utils/LocaleUtil";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const TWO_MONTHS = 60 * 60 * 24 * 60;

const SET_COOKIE_OPTS = {
  ...BHAPTICS_COOKIE_OPTS,
  maxAge: TWO_MONTHS,
};

export function readCountryFromCookie(): string {
  return parseCountryCode(getCookie(COUNTRY_CODE_KEY)) ?? "US";
}

// SSR과 CSR이 다른 쿠키 값을 읽어 hydration mismatch가 나는 것을 막기 위해
// 스토어 초기값은 빈 문자열로 두고, _app 의 useEffect 에서 마운트 후 1회 hydrate 한다.
export function hydrateLocaleStore() {
  localeStore.setState({ countryCode: readCountryFromCookie() });
}

type DialogType =
  | "select-locale-dialog"
  | "select-geo-based-locale-dialog"
  | "non-shippable-dialog"
  | null;

interface LocaleState {
  countryCode: string;
  dialog: DialogType;

  setCountryCode: (countryCode: string) => void;
  setDialog: (dialog: DialogType) => void;
}

const localeStore = create<LocaleState>()(
  devtools(
    (set) => ({
      countryCode: "",
      dialog: null,
      setDialog: (dialog) => set({ dialog }),
      setCountryCode: (countryCode: string) => {
        const upperCountryCode = countryCode.toUpperCase();
        set({ countryCode: upperCountryCode });

        // 과거 다른 스코프로 set된 동일 이름 쿠키 제거 → 중복 등록 시 소비자(GE JS 등)가 어느
        // 쪽을 읽을지 비결정적이 되는 버그 방지 (docs/pitfalls.md). set 스코프(현재 host)를 포함해
        // 알려진 후보 스코프를 모두 지운다 — 현재 host 스코프 누락으로 dev 에서 중복 쌓이던 버그 수정.
        deleteCookieAllScopes(COUNTRY_CODE_KEY, deleteCookie);

        setCookie(COUNTRY_CODE_KEY, upperCountryCode, SET_COOKIE_OPTS);
      },
    }),
    { name: "bhaptics-locale-store" },
  ),
);

/**
 * 국가·언어 선택 다이얼로그(`SelectLocaleDialog`, `_app.tsx` 가 `dialog` 상태로 띄운다)를 연다.
 * GNB·푸터의 국가 버튼이 `setDialog("select-locale-dialog")` 로 여는 것과 **같은 경로**다.
 *
 * 훅이 아닌 이유: React 밖(GE SDK 이벤트 콜백 — #5843 웰컴 팝업 「change shipping country」)에서
 * 불러야 한다.
 */
export function openLocaleSwitcher() {
  localeStore.getState().setDialog("select-locale-dialog");
}

export const useLocale = () => {
  const router = useRouter();
  const { locale, pathname, query, replace } = router;
  const storedCountryCode = localeStore((s) => s.countryCode);
  const dialog = localeStore((s) => s.dialog);
  const setDialog = localeStore((s) => s.setDialog);
  const setCountryCode = localeStore((s) => s.setCountryCode);

  const parsedLocale = useMemo(() => parseLocale(locale || "en"), [locale]);
  const languageCode = parsedLocale.languageCode;

  const countryCode = useMemo(() => {
    if (!storedCountryCode) {
      return parsedLocale.countryCode;
    }

    return getResolvedCountryCode(locale || "en", storedCountryCode);
  }, [parsedLocale, storedCountryCode, locale]);

  const setLocale = async ({
    scroll = false,
    countryCode: nextCountryCode,
    languageCode: nextLanguageCode,
  }: {
    scroll?: boolean;
    countryCode: string;
    languageCode: LanguageCode;
  }) => {
    // 현재 resolved 값과 비교 — 실제로 바뀐 경우에만 아래에서 reload(no-op 재선택은 reload 안 함).
    const localeChanged =
      nextCountryCode !== countryCode || nextLanguageCode !== languageCode;
    // 엔진이 이미 떠 있는 GE 국가 경계 변경은 아래에서 reload 한다. 그 전, 이동 중 렌더에서 **새 국가의
    // USD 가격**이 잠깐 보였다(dev 실측 2026-10-12 안도라 → 알바니아: reload 직전 `AL | $ 529`). 국가를
    // 바꾸기 **전에** flag 대기 숨김을 걸어 reload 로 넘긴다 — 새 페이지는 부트 스크립트가 이어받는다.
    // reload 가 일어나지 않으면 `FLAG_PENDING_FALLBACK_MS` 뒤 뗀다.
    if (
      localeChanged &&
      document.getElementById(GLOBALE_SCRIPT_ID) &&
      (isGeMarkedCountry(countryCode) || isGeMarkedCountry(nextCountryCode))
    ) {
      const root = document.documentElement;
      root.setAttribute(FLAG_PENDING_ATTR, "");
      window.setTimeout(
        () => root.removeAttribute(FLAG_PENDING_ATTR),
        FLAG_PENDING_FALLBACK_MS,
      );
    }
    setCountryCode(nextCountryCode);
    // 고른 언어를 남긴다 — 미들웨어가 로케일 없는 요청에서 국가 기본 언어보다 우선한다(#5859).
    // 영어는 주소에 접두어가 없어 이게 없으면 「영어를 골랐다」를 알 수 없다. 스코프 처리는 국가 쿠키와 같다.
    deleteCookieAllScopes(LANGUAGE_PREFERENCE_KEY, deleteCookie);
    setCookie(LANGUAGE_PREFERENCE_KEY, nextLanguageCode, SET_COOKIE_OPTS);
    // `GlobalE_Data` 는 GE 엔진이 **이미 로드돼 있을 때만** 여기서 쓴다 — 아래 reload 뒤 엔진이 새 국가를
    // 읽게. 엔진이 없으면(flag OFF · KR · webview) 쓰지 않는다: 엔진을 넣는 순간 `GlobalEScripts` 가 그
    // 국가로 쓴다(KR → GE 국가는 그 effect 가 바뀐 countryCode 로 처음 주입하며 쓴다). flag OFF 인 prod 가
    // `.bhaptics.com` 에 쿠키를 남기면 dev 엔진이 그걸 먼저 읽어 무한 재기록했다(2026-10-12, docs §3.5).
    const geLoaded = !!document.getElementById(GLOBALE_SCRIPT_ID);
    if (geLoaded) {
      syncGlobalEDataCookie({
        countryCode: nextCountryCode,
        languageCode: nextLanguageCode,
      });
    }
    await replace({ pathname, query }, undefined, {
      locale: nextLanguageCode,
      scroll,
    });
    // Global-E 는 페이지 로드 시 `GlobalE_Data` 쿠키로 country/currency/culture 를 재판별한다.
    // soft nav(`router.replace`)로는 반영 안 됨(Kyuri 2026-06-30 확정) → 실제 변경 시 full
    // reload 로 GE 재초기화. docs/global-e-storefront.md §3.5. 단 GE 와 무관한 변경엔 reload
    // 하지 않는다(무관 사용자 full reload 회귀 방지) — 두 조건으로 게이트:
    //
    //   1) `geRelevant`: 이 변경이 GE 대상인가(isGeMarkedCountry=non-KR). KR 엔 GE 스크립트가
    //      주입되지 않는다(`GlobalEScripts`, 2026-10-12~ — docs §1.1).
    //      현재도 다음도 KR 이면 GE 무관 → skip(KR 내부 ko↔en 토글이 홈마켓에 불필요 reload
    //      되는 것 방지). 한쪽이라도 GE 국가면 경계 이동(통화 전환 정합)이라 reload.
    //   2) `#globaleScript` 존재: GE 스크립트가 실제 주입됐나. KR 출발(KR → GE 국가)은 이동 전엔 없지만
    //      `GlobalEScripts` 가 이동 **도중에** 바뀐 countryCode 로 처음 주입하며 `GlobalE_Data` 도 그 국가로
    //      쓴다 — 그래서 이동 뒤엔 있다(아래 ⚠️ 의 비교로 reload 를 건너뛴다). flag off(prod 현재)/webview 는
    //      DOM 부재로 자동 제외. flag 직독 불가 — useLocale 은 Header(QueryClientProvider 밖)
    //      호출이라 RQ 훅 넣으면 크래시(docs/pitfalls.md §1). 극초기 flag-resolve 전 변경은
    //      skip 되지만 엔진을 넣을 때 `GlobalEScripts` 가 확정 국가로 쓰므로 benign.
    //
    //   ⚠️ 엔진 존재는 **이동 뒤에 다시 본다.** 이동 중에 flag 가 풀리거나(null→true) 확정 국가가 바뀌면
    //   `GlobalEScripts` 가 그 사이에 엔진을 넣는데, 그때 쓴 쿠키는 중간값(URL 로케일 파생 국가 · 이전 언어)
    //   일 수 있다. 그 경우엔 새 값으로 다시 쓰고 reload 해 엔진이 고른 국가로 다시 뜨게 한다.
    //   단 엔진이 **이미 고른 값으로 떴으면**(로더의 `data-ge-country`·`data-ge-language`) reload 하지 않는다.
    const geRelevant =
      isGeMarkedCountry(countryCode) || isGeMarkedCountry(nextCountryCode);
    const loaderNow = document.getElementById(GLOBALE_SCRIPT_ID);
    if (localeChanged && geRelevant && loaderNow) {
      if (!geLoaded) {
        // 이동 중에 들어온 엔진이 **이미 고른 값으로 떴으면** reload 하지 않는다 — 다시 띄워도 같은 값을
        // 읽는다. reload 하면 첫 화면이 쿠키를 읽기 전 URL 로케일 폴백(`/en` → US)으로 그려져 국가 표시가
        // KR → US → AL 로 깜빡였다(2026-10-12 실측). 값은 `GlobalEScripts` 가 주입 직전에 쓴 쿠키와 같다.
        if (
          loaderNow.dataset.geCountry === nextCountryCode.toUpperCase() &&
          loaderNow.dataset.geLanguage === nextLanguageCode
        ) {
          return;
        }
        syncGlobalEDataCookie({
          countryCode: nextCountryCode,
          languageCode: nextLanguageCode,
        });
      }
      window.location.reload();
    }
  };

  return {
    locale,
    countryCode,
    /**
     * `countryCode` 가 **쿠키에서 확정된 값인가.** `false` 면 위 `useMemo` 의 폴백
     * (= URL 로케일 파생 국가, `/en` → `US`)이다.
     *
     * ## 왜 필요한가
     *
     * `hydrateLocaleStore()` 는 `_app` 의 **부모 effect** 에서 돌고, 화면 컴포넌트의 effect 는
     * **자식이라 그보다 먼저** 돈다. 그래서 마운트 시 1회만 발화하는 effect 는 쿠키 값이 아니라
     * URL 로케일 국가를 보게 된다. deps 에 `countryCode` 가 있는 effect 는 재실행으로 **교정**되지만
     * 1회 발화는 교정될 자리가 없다. (`GlobalEScripts` 는 엔진 주입·`GlobalE_Data` 쓰기를 이 값이
     * `true` 가 될 때까지 미룬다 — 한 번 넣은 엔진은 되돌릴 수 없어서.)
     *
     * 이 값이 왜 완전한 신호인가: `readCountryFromCookie()` 는 쿠키가 없어도 `"US"` 를 돌려주므로
     * (`:29`) hydrate 가 끝나면 `storedCountryCode` 는 **반드시 비어 있지 않다.** 즉 빈 문자열은
     * "아직 hydrate 전" 과 정확히 동치다 — 새 상태 필드를 두지 않는 이유다.
     *
     * ⚠️ **분석 이벤트 소비자는 이걸 직접 쓰지 말고 `useFunnelValueEffect` 를 쓴다** —
     * 통화 축(`globalEEnabled` 로딩 창)이 하나 더 있다. `src/libs/analytics/use-funnel-value-effect.ts`.
     */
    isCountryHydrated: !!storedCountryCode,
    languageCode,
    setLocale,
    dialog,
    setCountryCode,
    setDialog,
    getLanguageName: () => {
      return LANGUAGE_NAME_MAP[languageCode] || LANGUAGE_NAME_MAP["en"];
    },
  };
};
